import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  LineChart,
  Line,
} from "recharts";

import cheshireWestLogo from "./assets/inhouse_blue_small_Cheshire_West (3).png";
import destinationsChartImage from "./assets/destinations_chart_square_blank.png";

import "./styles.css";

export const ChatbotDashboard = () => {
  const BRAND_COLORS = {
    blue: "#003366",
    yellow: "#FFC258",
    purple: "#7A5E95",
    aqua: "#90D29E",
    teal: "#007F99",
    pink: "#FFAB91",
    plum: "#B02058",
    sky: "#C0E1F4",
    lightPurple: "#A087B6",
  };

  const outcomesData = [
    {
      name: "Outcomes",
      positive: 88.75,
      negative: 11.25,
    },
  ];

  const issuesData = [
    { issue: "Speak to a real person", count: 181 },
    { issue: "Search using your own words", count: 122 },
    { issue: "Benefits", count: 74 },
    { issue: "Heating bank", count: 62 },
    { issue: "Employment & Work", count: 48 },
    { issue: "Legal", count: 32 },
    { issue: "Housing", count: 26 },
    { issue: "Consumer goods & services", count: 25 },
    { issue: "Relationships & Family", count: 20 },
    { issue: "Can't find what you're looking for", count: 13 },
  ].sort((a, b) => b.count - a.count);

  const wardsData = [
    { location: "Chester City & the Garden Quarter", count: 65 },
    { location: "Winsford Over & Verdin", count: 40 },
    { location: "Blacon", count: 32 },
    { location: "Central & Grange", count: 28 },
    { location: "Northwich Leftwich", count: 25 },
    { location: "Newton & Hoole", count: 22 },
    { location: "Shakerley", count: 20 },
    { location: "Northwich Winnington & Castle", count: 18 },
    { location: "Westminster", count: 16 },
    { location: "Upton", count: 12 },
  ].sort((a, b) => b.count - a.count);

  const hourlyData = [
    { time: "00:00", count: 0 },
    { time: "01:00", count: 0 },
    { time: "02:00", count: 0 },
    { time: "03:00", count: 0 },
    { time: "04:00", count: 0 },
    { time: "05:00", count: 0 },
    { time: "06:00", count: 0 },
    { time: "07:00", count: 1 },
    { time: "08:00", count: 6 },
    { time: "09:00", count: 12 },
    { time: "10:00", count: 26 },
    { time: "11:00", count: 30 },
    { time: "12:00", count: 28 },
    { time: "13:00", count: 35 },
    { time: "14:00", count: 31 },
    { time: "15:00", count: 29 },
    { time: "16:00", count: 21 },
    { time: "17:00", count: 15 },
    { time: "18:00", count: 11 },
    { time: "19:00", count: 8 },
    { time: "20:00", count: 4 },
    { time: "21:00", count: 2 },
    { time: "22:00", count: 1 },
    { time: "23:00", count: 0 },
  ];

  const weeklyData = [
    { day: "M", count: 110 },
    { day: "Tu", count: 105 },
    { day: "W", count: 90 },
    { day: "Th", count: 80 },
    { day: "F", count: 75 },
    { day: "Sa", count: 30 },
    { day: "Su", count: 25 },
  ];

  const chartContainerStyle = {
    backgroundColor: "white",
    border: "1px solid #e5e7eb",
    padding: "8px",
    flex: "1",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  };

  const bottomChartContainerStyle = {
    backgroundColor: "white",
    border: "1px solid #e5e7eb",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  };

  const topRowStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "4px",
    marginBottom: "32px",
  };

  const bottomRowFlexStyle = {
    display: "flex",
    gap: "8px",
    alignItems: "stretch",
  };

  const CustomOutcomesTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const positiveData = payload.find((p) => p.dataKey === "positive");
      const negativeData = payload.find((p) => p.dataKey === "negative");
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            padding: "10px",
            zIndex: 1000,
          }}
        >
          {positiveData && (
            <p
              style={{ color: positiveData.color }}
            >{`Positive: ${positiveData.value}%`}</p>
          )}
          {negativeData && (
            <p
              style={{ color: negativeData.color }}
            >{`Negative: ${negativeData.value}%`}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#e5e7eb",
        fontFamily: "Open Sans, sans-serif",
        paddingBottom: "32px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #e5e7eb",
          padding: "16px 24px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: "1152px",
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: BRAND_COLORS.blue,
              color: "white",
              padding: "12px 24px",
              borderRadius: "4px",
              flexGrow: 1,
              marginRight: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h1 style={{ fontSize: "24px", fontWeight: "bold" }}>
              Advicebot key statistics
            </h1>
            <p style={{ fontSize: "14px", fontWeight: "600" }}>Apr-Jun 2025</p>
          </div>
          <img
            src={cheshireWestLogo}
            alt="Cheshire West Logo"
            style={{ height: "40px", width: "auto" }}
          />
        </div>
      </div>

      <div
        style={{ maxWidth: "1152px", margin: "0 auto", padding: "4px 16px" }}
      >
        <div style={topRowStyle}>
          <div
            style={{
              ...chartContainerStyle,
              alignItems: "flex-start",
              justifyContent: "flex-start",
              padding: "4px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: BRAND_COLORS.blue,
                marginBottom: "16px",
              }}
            >
              Summary
            </h2>
            <div
              style={{
                backgroundColor: BRAND_COLORS.blue,
                color: "white",
                padding: "8px",
                textAlign: "center",
                width: "100%",
                marginBottom: "8px",
                borderRadius: "4px",
                boxSizing: "border-box",
              }}
            >
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "4px",
                  color: BRAND_COLORS.yellow,
                  fontWeight: "bold",
                }}
              >
                Clients
              </p>
              <p style={{ fontSize: "24px", fontWeight: "bold" }}>515</p>
            </div>
            <div
              style={{
                textAlign: "center",
                width: "100%",
                marginBottom: "8px",
              }}
            >
              <p
                style={{
                  fontSize: "16px",
                  color: BRAND_COLORS.blue,
                  fontWeight: "bold",
                }}
              >
                Proportion of website visitors who used Advicebot
              </p>
              <p
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: BRAND_COLORS.blue,
                }}
              >
                5.22%
              </p>
            </div>

            <div
              style={{
                width: "100%",
                borderTop: "1px solid #e5e7eb",
                paddingTop: "4px",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "2px",
                  color: BRAND_COLORS.blue,
                  textAlign: "center",
                }}
              >
                Outcomes
              </h3>
              <div style={{ width: "100%", flexGrow: 1 }}>
                <ResponsiveContainer width="100%" height={70}>
                  <BarChart
                    layout="vertical"
                    data={outcomesData}
                    barSize={50}
                    stackOffset="expand"
                    margin={{ top: 0, right: 30, left: 30, bottom: 0 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" hide />
                    <Tooltip content={<CustomOutcomesTooltip />} />
                    <Bar
                      dataKey="positive"
                      stackId="a"
                      fill={BRAND_COLORS.blue}
                    >
                      <LabelList
                        dataKey="positive"
                        position="insideRight"
                        formatter={(value) => `${value}%`}
                        fill="white"
                      />
                    </Bar>
                    <Bar
                      dataKey="negative"
                      stackId="a"
                      fill={BRAND_COLORS.yellow}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "4px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "10px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: BRAND_COLORS.blue,
                        marginRight: "5px",
                      }}
                    ></span>
                    <span style={{ fontSize: "12px" }}>Positive</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "10px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: BRAND_COLORS.yellow,
                        marginRight: "5px",
                      }}
                    ></span>
                    <span style={{ fontSize: "12px" }}>Negative</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ ...chartContainerStyle, alignItems: "stretch" }}>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: BRAND_COLORS.blue,
              }}
            >
              Top Issues
            </h2>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart
                layout="vertical"
                data={issuesData}
                margin={{ top: 2, right: 30, left: 20, bottom: 2 }}
              >
                <XAxis type="number" hide={true} />
                <YAxis
                  type="category"
                  dataKey="issue"
                  axisLine={false}
                  tickLine={false}
                  width={150}
                />
                <Bar dataKey="count" fill={BRAND_COLORS.blue}>
                  <LabelList dataKey="count" position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ ...chartContainerStyle, alignItems: "stretch" }}>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: BRAND_COLORS.blue,
              }}
            >
              Top Wards
            </h2>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart
                layout="vertical"
                data={wardsData}
                margin={{ top: 2, left: 20, right: 20, bottom: 2 }}
              >
                <XAxis type="number" dataKey="count" hide={true} />
                <YAxis
                  type="category"
                  dataKey="location"
                  axisLine={false}
                  tickLine={false}
                  width={150}
                />
                <Bar dataKey="count" fill={BRAND_COLORS.aqua}>
                  <LabelList dataKey="count" position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={bottomRowFlexStyle}>
          {/* Top Destinations */}
          <div
            style={{ ...bottomChartContainerStyle, flex: "1", height: "412px" }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: BRAND_COLORS.blue,
                marginBottom: "16px",
              }}
            >
              Top Destinations
            </h3>
            <div
              style={{ flex: "1", display: "flex", flexDirection: "column" }}
            >
              <img
                src={destinationsChartImage}
                alt="Top Destinations stacked bar chart"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  height: "320px",
                }}
              />
            </div>
          </div>
          {/* Hourly chart in the middle */}
          <div
            style={{ ...bottomChartContainerStyle, flex: "1", height: "412px" }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: BRAND_COLORS.blue,
                textAlign: "left",
                width: "100%",
                marginBottom: "16px",
              }}
            >
              Hourly Chat Distribution
            </h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={hourlyData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis dataKey="time" />
                <YAxis hide />
                <Tooltip />
                <Bar dataKey="count" fill={BRAND_COLORS.lightPurple}>
                  <LabelList dataKey="count" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Daily chart on the right */}
          <div
            style={{ ...bottomChartContainerStyle, flex: "1", height: "412px" }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: BRAND_COLORS.blue,
                textAlign: "left",
                width: "100%",
                marginBottom: "16px",
              }}
            >
              Daily Chat Distribution
            </h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={weeklyData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis dataKey="day" />
                <YAxis hide />
                <Tooltip />
                <Bar dataKey="count" fill={BRAND_COLORS.aqua} barSize={20}>
                  <LabelList dataKey="count" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotDashboard;
